import { AccountStatus } from 'src/utils/enums/portfolio.enums';
import Chevron from 'src/components/dashboard/components/navigationMenu/components/chevron';
import { formatDate } from 'src/utils/formatDate';
import { formatMoney } from 'src/utils/formatMoney';
import { Portfolio } from 'src/services/models/portfolio.service';
import { useLocalization } from 'src/utils/useLocalization';
import { Dispatch, memo, SetStateAction } from 'react';
import './styles.scss';

export interface ITableContents {
    items: any[];
    sortType: string;
    sortOrder: boolean;
    setSortType: Dispatch<SetStateAction<string>>;
    setSortOrder: Dispatch<SetStateAction<boolean>>;
    acctFilter: string;
    setDetailAccountNumber: Dispatch<SetStateAction<string | null>>;
}

export interface PortfolioTableItemData {
    data: Portfolio;
    setDetailAccountNumber: Dispatch<SetStateAction<string | null>>;
}

const TableItemComponent: React.FC<PortfolioTableItemData> = ({
    data,
    setDetailAccountNumber,
}) => {
    const { portfolioManager } = useLocalization();
    const parseProgram = (value: string) => {
        if (value.includes('|')) return value.split('|').join(' ');

        return value;
    };
    const selectAccount = (accountType: string) => {
        const value = accountType.toLocaleLowerCase();
        if (value.includes('loan')) return portfolioManager.PORTFOLIO_LOAN;
        if (value.includes('lease')) return portfolioManager.PORTFOLIO_LEASE;
        if (value.includes('vw')) return portfolioManager.PORTFOLIO_VW_SELECT;
        return portfolioManager.PORTFOLIO_AUDI_SELECT;
    };
    return (
        <div className='row'>
            <div className='table-row col-1'>
                <span className='list-label'>
                    {portfolioManager.PORTFOLIO_CUSTOMER_LABEL}
                </span>
                <span
                    className='underline'
                    onClick={() => setDetailAccountNumber(data.accountNumber)}
                    aria-hidden={'true'}
                >
                    {data.customerFullName}
                </span>
            </div>
            <div className='table-row col-2'>
                <span className='list-label'>
                    {portfolioManager.PORTFOLIO_ACCOUNT_LABEL}
                </span>
                <span>{data.accountNumber}</span>
            </div>
            <div className='table-row col-3'>
                <span className='list-label'>
                    {portfolioManager.PORTFOLIO_BASE_PAYMENT_LABEL}
                </span>
                <span>{formatMoney(data.monthlyBasePayment)}</span>
            </div>
            <div className='table-row col-4'>
                <span className='list-label'>
                    {portfolioManager.PORTFOLIO_EST_EQUITY_LABEL}
                </span>
                <span>{formatMoney(data.blackEquity)}</span>
            </div>
            <div className='table-row col-5'>
                <span className='list-label'>
                    {portfolioManager.PORTFOLIO_CURRT_MATURITY_LABEL}
                </span>
                <span>
                    {data.maturityOrAcctStatusDate
                        ? formatDate(data.maturityOrAcctStatusDate)
                        : 'N/A'}
                </span>
            </div>
            <div className='table-row col-6'>
                <span className='list-label'>
                    {portfolioManager.PORTFOLIO_MODEL_LABEL}
                </span>
                <span>{data.vehicleModel}</span>
            </div>
            <div className='table-row col-7'>
                <span className='list-label'>
                    {portfolioManager.PORTFOLIO_ACCT_TYPE_LABEL}
                </span>
                <span>{selectAccount(data.accountType)}</span>
            </div>
            <div className='table-row col-8'>
                <span className='list-label'>
                    {portfolioManager.PORTFOLIO_PROGRAM_LABEL}
                </span>
                <span>
                    {!data.programEligibility
                        ? portfolioManager.PORTFOLIO_NONE_LABEL
                        : parseProgram(data.programEligibility)}
                </span>
            </div>
        </div>
    );
};

const TableItem = memo(TableItemComponent);

export const Table: React.FC<ITableContents> = ({
    items,
    sortType,
    sortOrder,
    setSortType,
    setSortOrder,
    acctFilter,
    setDetailAccountNumber,
}) => {
    const chevronColor = '#363b49';
    const { portfolioManager } = useLocalization();

    /**
     * Function to sort based on the property and sort order
     * @param property
     * @param order
     */
    const applySort = (property: string, order = true) => {
        if (sortType == property) {
            setSortOrder(!sortOrder);
        } else {
            setSortType(property);
            setSortOrder(order);
        }
    };

    /**
     * Function to render sorting buttons on table header
     * @param property
     * @param clickhandler
     * @returns
     */
    const renderTableSort = (property: string, clickhandler: any) => {
        return (
            <span className='table-header__order'>
                <span
                    onClick={() => {
                        clickhandler(property);
                    }}
                    aria-hidden='true'
                >
                    <Chevron
                        className={`accordion__icon ${
                            sortOrder && sortType == property
                                ? 'rotate-down'
                                : 'rotate-up'
                        }`}
                        width={10}
                        fill={chevronColor}
                    />
                </span>
                {sortType != property ? (
                    <span
                        onClick={() => {
                            clickhandler(property, false);
                        }}
                        aria-hidden='true'
                    >
                        <Chevron
                            className='accordion__icon rotate-down'
                            width={10}
                            fill={chevronColor}
                        />
                    </span>
                ) : null}
            </span>
        );
    };
    return (
        <div
            className={`portfolio-table-wrapper ${
                acctFilter === AccountStatus.ACTIVE ? 'active' : 'terminated'
            }`}
        >
            <div className='table' id='portfolio-table'>
                <div className='row header-row hidden-in-list'>
                    <div className='table-header col-1'>
                        <span className='u-text-wrap'>
                            {portfolioManager.PORTFOLIO_CUSTOMER_LABEL}
                        </span>
                    </div>
                    <div className='table-header col-2'>
                        <span className='u-text-wrap'>
                            {portfolioManager.PORTFOLIO_ACCOUNT_LABEL}
                        </span>
                    </div>
                    <div className='table-header col-3'>
                        <span className='u-text-wrap'>
                            {portfolioManager.PORTFOLIO_BASE_PAYMENT_LABEL}
                        </span>
                    </div>
                    <div className='table-header col-4'>
                        <span className='u-text-wrap'>
                            {portfolioManager.PORTFOLIO_EST_EQUITY_LABEL}
                        </span>
                        {renderTableSort('blackEquity', applySort)}
                    </div>
                    <div className={'table-header col-5'}>
                        <span className='u-text-wrap'>
                            {acctFilter === AccountStatus.ACTIVE
                                ? portfolioManager.PORTFOLIO_CURRT_MATURITY_LABEL
                                : portfolioManager.PORTFOLIO_PAY_DATE_LABEL}
                        </span>
                        {renderTableSort(
                            acctFilter === AccountStatus.ACTIVE
                                ? 'maturityDate'
                                : 'termStatusDate',
                            applySort,
                        )}
                    </div>
                    <div className='table-header col-6'>
                        <span className='u-text-wrap'>
                            {portfolioManager.PORTFOLIO_MODEL_LABEL}
                        </span>
                        {renderTableSort('vehicleModel', applySort)}
                    </div>
                    <div className='table-header col-7'>
                        <span className='u-text-wrap'>
                            {portfolioManager.PORTFOLIO_ACCT_TYPE_LABEL}
                        </span>
                    </div>
                    <div className='table-header col-8'>
                        <span className='u-text-wrap'>
                            {portfolioManager.PORTFOLIO_PROGRAM_LABEL}
                        </span>
                    </div>
                </div>
                {items.map((item) => (
                    <TableItem
                        key={item.accountNumber}
                        data={{
                            ...item,
                            maturityOrAcctStatusDate:
                                acctFilter === AccountStatus.ACTIVE
                                    ? item.maturityDate
                                    : item.termStatusDate,
                        }}
                        setDetailAccountNumber={setDetailAccountNumber}
                    />
                ))}
            </div>
        </div>
    );
};
